/**
 * StringConfigurator - PV String Configuration and Electrical Compatibility
 * 
 * Professional component for:
 * - Automatic string calculation
 * - MPPT assignment visualization
 * - Electrical compatibility verification
 * - Technical warnings display
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../lib/api';
import {
  Zap,
  Cable,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Loader2,
  RefreshCw,
  Settings2,
  Cpu,
  ArrowRight,
  Gauge,
  Thermometer,
  Activity
} from 'lucide-react';

// Status colors
const STATUS_COLORS = {
  compatibile: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2 },
  da_verificare: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle },
  non_compatibile: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: XCircle }
};

// Warning severity colors
const SEVERITY_COLORS = {
  error: 'bg-rose-100 border-rose-300 text-rose-800',
  warning: 'bg-amber-100 border-amber-300 text-amber-800',
  info: 'bg-blue-100 border-blue-300 text-blue-800'
};

// Animation constants for framer-motion
const SLIDE_IN_ANIMATION = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 }
};

const FADE_IN_Y_ANIMATION = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.2 }
};

const FADE_DOWN_ANIMATION = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 }
};

const EXPAND_ANIMATION = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 }
};

// MPPT Diagram Component
function MPPTDiagram({ mpptConfigs, moduleSpecs, inverterSpecs }) {
  if (!mpptConfigs || mpptConfigs.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-400">
        <Cable className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>Nessuna stringa configurata</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {mpptConfigs.map((mppt, idx) => (
        <motion.div
          key={mppt.mppt_id}
          {...SLIDE_IN_ANIMATION}
          transition={{ delay: idx * 0.1 }}
          className="border border-zinc-200 rounded-xl p-4 bg-gradient-to-r from-zinc-50 to-white"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Cpu className="h-4 w-4 text-indigo-600" />
              </div>
              <span className="font-semibold text-zinc-900">MPPT {mppt.mppt_id}</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-zinc-500">
                <span className="font-mono font-semibold text-zinc-700">{(mppt.total_power_wp / 1000).toFixed(2)}</span> kWp
              </span>
              <span className="text-zinc-500">
                <span className="font-mono font-semibold text-zinc-700">{mppt.total_current_a.toFixed(1)}</span> A
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mppt.strings.map((string) => (
              <div
                key={string.string_id}
                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-zinc-100 hover:border-zinc-200 transition-colors"
              >
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(string.num_modules, 6) }).map((_, i) => (
                    <div
                      key={`mod-${string.string_id}-${i}`}
                      className="w-4 h-6 bg-gradient-to-b from-blue-400 to-blue-600 rounded-sm shadow-sm"
                      title={`Modulo ${i + 1}`}
                    />
                  ))}
                  {string.num_modules > 6 && (
                    <span className="text-xs text-zinc-500 ml-1">+{string.num_modules - 6}</span>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-zinc-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900">
                    Stringa {string.string_id}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">
                    {string.num_modules} mod · {string.vmp_string_v}V · {string.power_wp}Wp
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// Technical Parameters Display
function TechnicalParameters({ design, moduleSpecs, inverterSpecs }) {
  if (!design) return null;

  const params = [
    {
      label: 'Voc Stringa (max)',
      value: design.voc_string_max_v,
      unit: 'V',
      threshold: inverterSpecs?.tensione_max_ingresso_v,
      thresholdLabel: 'Max inverter',
      icon: Zap,
      tooltip: 'Tensione a circuito aperto della stringa a -10°C'
    },
    {
      label: 'Vmp Stringa',
      value: design.vmp_string_v,
      unit: 'V',
      threshold: inverterSpecs?.range_mppt_v ? `${inverterSpecs.range_mppt_v}` : null,
      thresholdLabel: 'Range MPPT',
      icon: Activity,
      tooltip: 'Tensione al punto di massima potenza a STC'
    },
    {
      label: 'Isc Stringa',
      value: design.isc_string_a,
      unit: 'A',
      threshold: inverterSpecs?.corrente_max_ingresso_a,
      thresholdLabel: 'Max inverter',
      icon: Gauge,
      tooltip: 'Corrente di cortocircuito della stringa'
    },
    {
      label: 'Rapporto DC/AC',
      value: design.dc_ac_ratio,
      unit: '',
      threshold: '0.8 - 1.3',
      thresholdLabel: 'Range ottimale',
      icon: Settings2,
      tooltip: 'Rapporto tra potenza DC moduli e potenza AC inverter'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {params.map((param) => {
        const Icon = param.icon;
        return (
          <TooltipProvider key={param.label}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="p-3 rounded-xl bg-zinc-50 border border-zinc-100 hover:border-zinc-200 transition-colors cursor-help">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-4 w-4 text-zinc-400" />
                    <span className="text-xs text-zinc-500 font-medium">{param.label}</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-mono text-xl font-bold text-zinc-900">
                      {typeof param.value === 'number' ? param.value.toFixed(param.unit === 'A' ? 2 : param.unit === '' ? 2 : 1) : '-'}
                    </span>
                    {param.unit && <span className="text-sm text-zinc-500">{param.unit}</span>}
                  </div>
                  {param.threshold && (
                    <div className="text-xs text-zinc-400 mt-1">
                      {param.thresholdLabel}: {param.threshold}
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-zinc-900 text-zinc-50 text-xs px-3 py-2 rounded-lg max-w-xs">
                {param.tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

// Warnings Display
function WarningsDisplay({ warnings }) {
  if (!warnings || warnings.length === 0) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
        <span className="text-emerald-700 font-medium">Nessun problema rilevato - Configurazione compatibile</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {warnings.map((warning, idx) => (
        <motion.div
          key={`warning-${warning.code || warning.message}`}
          {...FADE_DOWN_ANIMATION}
          transition={{ delay: idx * 0.05 }}
          className={`flex items-start gap-3 p-3 rounded-xl border ${SEVERITY_COLORS[warning.severity]}`}
        >
          {warning.severity === 'error' ? (
            <XCircle className="h-5 w-5 mt-0.5 shrink-0" />
          ) : warning.severity === 'warning' ? (
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
          ) : (
            <Info className="h-5 w-5 mt-0.5 shrink-0" />
          )}
          <div className="flex-1">
            <p className="font-medium">{warning.message}</p>
            {warning.value !== null && warning.threshold !== null && (
              <p className="text-sm opacity-75 mt-1">
                Valore: {warning.value?.toFixed(2)} | Limite: {warning.threshold}
              </p>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// Main Component
export default function StringConfigurator({
  moduleSpecs,
  inverterSpecs,
  totalModules,
  onDesignChange
}) {
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customMode, setCustomMode] = useState(false);
  const [customStrings, setCustomStrings] = useState(null);
  const [customModulesPerString, setCustomModulesPerString] = useState(null);
  const [limits, setLimits] = useState(null);

  // Calculate string design
  const calculateDesign = useCallback(async () => {
    if (!moduleSpecs || !inverterSpecs || totalModules === 0) {
      setDesign(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/pv-design/calculate-strings', {
        module_specs: moduleSpecs,
        inverter_specs: inverterSpecs,
        total_modules: totalModules,
        custom_strings: customMode ? customStrings : null,
        custom_modules_per_string: customMode ? customModulesPerString : null
      });

      setDesign(response.data);
      if (onDesignChange) {
        onDesignChange(response.data);
      }
    } catch (err) {
      console.error('String calculation error:', err);
      setError(err.response?.data?.detail || 'Errore nel calcolo stringhe');
    } finally {
      setLoading(false);
    }
  }, [moduleSpecs, inverterSpecs, totalModules, customMode, customStrings, customModulesPerString, onDesignChange]);

  // Load limits
  const loadLimits = useCallback(async () => {
    if (!moduleSpecs || !inverterSpecs) return;

    try {
      const response = await api.post('/pv-design/string-limits', {
        module_specs: moduleSpecs,
        inverter_specs: inverterSpecs
      });
      setLimits(response.data);
    } catch (err) {
      console.error('Limits error:', err);
    }
  }, [moduleSpecs, inverterSpecs]);

  // Calculate on input change
  useEffect(() => {
    calculateDesign();
    loadLimits();
  }, [calculateDesign, loadLimits]);

  // Status display
  const statusConfig = design?.status ? STATUS_COLORS[design.status] : null;
  const StatusIcon = statusConfig?.icon || Info;

  if (!moduleSpecs || !inverterSpecs) {
    return (
      <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
        <CardContent className="py-12 text-center">
          <Settings2 className="h-12 w-12 mx-auto mb-4 text-zinc-300" />
          <p className="text-zinc-500">Seleziona moduli e inverter per visualizzare la configurazione stringhe</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="string-configurator">
        {/* Status Banner */}
        {design && statusConfig && (
          <motion.div
            {...FADE_DOWN_ANIMATION}
            className={`flex items-center gap-3 p-4 rounded-xl border ${statusConfig.bg} ${statusConfig.border}`}
          >
            <StatusIcon className={`h-6 w-6 ${statusConfig.text}`} />
            <div className="flex-1">
              <span className={`font-semibold ${statusConfig.text}`}>
                {design.status === 'compatibile' ? 'Configurazione Compatibile' :
                 design.status === 'da_verificare' ? 'Configurazione da Verificare' :
                 'Configurazione Non Compatibile'}
              </span>
              <p className={`text-sm ${statusConfig.text} opacity-75`}>
                {design.num_strings} stringhe da {design.modules_per_string} moduli · DC/AC ratio: {design.dc_ac_ratio}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={calculateDesign}
              disabled={loading}
              className={statusConfig.text}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: MPPT Configuration */}
          <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                <Cable className="h-5 w-5 text-zinc-500" />
                Configurazione Stringhe e MPPT
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-rose-500">
                  <XCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>{error}</p>
                </div>
              ) : (
                <MPPTDiagram
                  mpptConfigs={design?.mppt_configs}
                  moduleSpecs={moduleSpecs}
                  inverterSpecs={inverterSpecs}
                />
              )}

              {/* Custom Configuration Toggle */}
              {limits && (
                <div className="mt-6 pt-4 border-t border-zinc-100">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-sm font-medium text-zinc-700">Configurazione manuale</Label>
                    <Button
                      variant={customMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setCustomMode(!customMode);
                        if (!customMode) {
                          setCustomStrings(design?.num_strings || 2);
                          setCustomModulesPerString(design?.modules_per_string || 10);
                        } else {
                          setCustomStrings(null);
                          setCustomModulesPerString(null);
                        }
                      }}
                    >
                      {customMode ? 'Auto' : 'Manuale'}
                    </Button>
                  </div>

                  {customMode && (
                    <motion.div
                      {...EXPAND_ANIMATION}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-zinc-500">Numero stringhe</Label>
                          <Input
                            type="number"
                            value={customStrings || ''}
                            onChange={(e) => setCustomStrings(parseInt(e.target.value) || 1)}
                            min={1}
                            max={limits.max_strings}
                            className="mt-1"
                          />
                          <span className="text-xs text-zinc-400">Max: {limits.max_strings}</span>
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-500">Moduli per stringa</Label>
                          <Input
                            type="number"
                            value={customModulesPerString || ''}
                            onChange={(e) => setCustomModulesPerString(parseInt(e.target.value) || 1)}
                            min={limits.min_modules_per_string}
                            max={limits.max_modules_per_string}
                            className="mt-1"
                          />
                          <span className="text-xs text-zinc-400">
                            Range: {limits.min_modules_per_string}-{limits.max_modules_per_string}
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={calculateDesign}
                        className="w-full"
                        disabled={loading}
                      >
                        Ricalcola
                      </Button>
                    </motion.div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Technical Parameters & Warnings */}
          <div className="space-y-6">
            {/* Technical Parameters */}
            <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-zinc-500" />
                  Parametri Elettrici
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TechnicalParameters
                  design={design}
                  moduleSpecs={moduleSpecs}
                  inverterSpecs={inverterSpecs}
                />
              </CardContent>
            </Card>

            {/* Warnings */}
            <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-zinc-500" />
                  Verifica Compatibilità
                </CardTitle>
              </CardHeader>
              <CardContent>
                <WarningsDisplay warnings={design?.warnings} />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Summary Stats */}
        {design && (
          <Card className="bg-gradient-to-r from-zinc-900 to-zinc-800 text-white border-0 rounded-2xl shadow-lg">
            <CardContent className="py-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold">{design.total_modules}</div>
                  <div className="text-xs text-zinc-400">Moduli totali</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold">{design.num_strings}</div>
                  <div className="text-xs text-zinc-400">Stringhe</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold">{design.modules_per_string}</div>
                  <div className="text-xs text-zinc-400">Moduli/stringa</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold">{design.total_dc_power_kwp}</div>
                  <div className="text-xs text-zinc-400">kWp totali</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold">{design.dc_ac_ratio}</div>
                  <div className="text-xs text-zinc-400">DC/AC ratio</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}
